import { useCallback, useEffect, useState } from "react";
import { Bell, ChevronDown, ChevronRight, Plus, Save, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { t } from "@/lib/i18n";
import { PageHeader } from "@/components/layout/PageHeader";
import { Card } from "@/components/ui/Card";
import { FormMessage, type Feedback } from "@/components/ui/FormMessage";
import {
  ALERT_LEVELS,
  EMPTY_TEAMS_ALERT,
  effectiveCollapseWindow,
  effectiveMaxPerMinute,
  type AlertChannelType,
  type GenericResponse,
  type TeamsAlert,
  type TeamsAlertsConfig,
} from "@/lib/types";

const CHANNEL_TYPES: { value: AlertChannelType; label: string }[] = [
  { value: "teams", label: "Microsoft Teams" },
  { value: "webhook", label: "Webhook" },
  { value: "email", label: "E-Mail" },
  { value: "nextcloud_talk", label: "Nextcloud Talk" },
];

export function AlertingPage() {
  const [alerts, setAlerts] = useState<TeamsAlert[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState<Feedback>(null);
  const [dirty, setDirty] = useState(false);
  const [expanded, setExpanded] = useState<Set<number>>(new Set([0]));

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.get<TeamsAlertsConfig>("/api/settings/teams-alerts");
      setAlerts(data.alerts ?? []);
      setDirty(false);
    } catch (e) {
      setFeedback({ kind: "error", text: e instanceof Error ? e.message : t("Laden fehlgeschlagen") });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  const updateAlert = (idx: number, patch: Partial<TeamsAlert>) => {
    setAlerts((cur) => cur.map((a, i) => (i === idx ? { ...a, ...patch } : a)));
    setDirty(true);
  };

  const addAlert = () => {
    setAlerts((cur) => [...cur, { ...EMPTY_TEAMS_ALERT, name: t("Channel {n}", { n: cur.length + 1 }) }]);
    setExpanded((cur) => new Set([...cur, alerts.length]));
    setDirty(true);
  };

  const removeAlert = (idx: number) => {
    const a = alerts[idx];
    if (!confirm(t("Channel „{name}\" wirklich entfernen?", { name: a.name || t("(unbenannt)") }))) return;
    setAlerts((cur) => cur.filter((_, i) => i !== idx));
    setDirty(true);
  };

  const onSave = async () => {
    setSaving(true);
    setFeedback(null);
    const body: TeamsAlertsConfig = {
      alerts: alerts.map((a) => ({
        ...a,
        name: a.name.trim(),
        webhook_url: a.webhook_url.trim(),
        email_to: a.email_to.trim(),
        talk_secret: (a.talk_secret ?? "").trim(),
        patterns: a.patterns.map((p) => p.trim()).filter(Boolean),
        levels: a.levels.length === 0 ? ["ANY"] : a.levels,
      })),
    };
    try {
      const resp = await api.post<GenericResponse>("/api/settings/teams-alerts", body);
      if (resp.success) {
        setFeedback({ kind: "success", text: resp.message });
        setDirty(false);
      } else {
        setFeedback({ kind: "error", text: resp.message });
      }
    } catch (e) {
      setFeedback({ kind: "error", text: e instanceof Error ? e.message : t("Speichern fehlgeschlagen") });
    } finally {
      setSaving(false);
    }
  };

  const toggleExpanded = (idx: number) => {
    setExpanded((cur) => {
      const next = new Set(cur);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  return (
    <div>
      <PageHeader
        title={t("Alarmierung")}
        description={t("Alarm-Kanäle: Teams, Webhook oder E-Mail. Logs, die einem Pattern entsprechen, werden automatisch zugestellt.")}
        actions={
          <>
            <button
              type="button"
              onClick={addAlert}
              className="inline-flex items-center gap-1.5 rounded-md border border-border bg-card px-2.5 py-1.5 text-xs font-medium hover:bg-muted"
            >
              <Plus className="h-3.5 w-3.5" /> {t("Channel")}
            </button>
            <button
              type="button"
              onClick={onSave}
              disabled={saving || !dirty}
              className="inline-flex items-center gap-1.5 rounded-md bg-primary px-2.5 py-1.5 text-xs font-medium text-primary-foreground disabled:opacity-50"
            >
              <Save className="h-3.5 w-3.5" /> {saving ? t("Speichern…") : dirty ? t("Speichern") : t("Gespeichert")}
            </button>
          </>
        }
      />

      <div className="flex flex-col gap-4 p-6">
        <FormMessage feedback={feedback} />

        {loading ? (
          <div className="text-sm text-muted-foreground">{t("Lade…")}</div>
        ) : alerts.length === 0 ? (
          <Card>
            <div className="flex flex-col items-center gap-3 py-6 text-center text-sm text-muted-foreground">
              <Bell className="h-6 w-6" />
              <div>{t("Noch keine Alarm-Kanäle eingerichtet.")}</div>
              <button
                type="button"
                onClick={addAlert}
                className="inline-flex items-center gap-1.5 rounded-md bg-primary px-2.5 py-1.5 text-xs font-medium text-primary-foreground"
              >
                <Plus className="h-3.5 w-3.5" /> {t("Ersten Channel anlegen")}
              </button>
            </div>
          </Card>
        ) : (
          alerts.map((alert, idx) => (
            <AlertCard
              key={idx}
              alert={alert}
              expanded={expanded.has(idx)}
              onToggle={() => toggleExpanded(idx)}
              onChange={(patch) => updateAlert(idx, patch)}
              onRemove={() => removeAlert(idx)}
            />
          ))
        )}
      </div>
    </div>
  );
}

function AlertCard({
  alert,
  expanded,
  onToggle,
  onChange,
  onRemove,
}: {
  alert: TeamsAlert;
  expanded: boolean;
  onToggle: () => void;
  onChange: (patch: Partial<TeamsAlert>) => void;
  onRemove: () => void;
}) {
  const patternsText = alert.patterns.join("\n");

  const toggleLevel = (level: string) => {
    if (level === "ANY") {
      onChange({ levels: ["ANY"] });
      return;
    }
    const current = alert.levels.filter((l) => l !== "ANY");
    const next = current.includes(level)
      ? current.filter((l) => l !== level)
      : [...current, level];
    onChange({ levels: next.length === 0 ? ["ANY"] : next });
  };

  return (
    <section
      className={
        "rounded-lg border bg-card " +
        (alert.enabled ? "border-border" : "border-dashed border-border opacity-70")
      }
    >
      <header className="flex items-center gap-3 px-4 py-3">
        <button
          type="button"
          onClick={onToggle}
          className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
          aria-label={expanded ? t("Einklappen") : t("Ausklappen")}
        >
          {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </button>

        <input
          type="text"
          value={alert.name}
          onChange={(e) => onChange({ name: e.target.value })}
          placeholder={t("Channel-Name")}
          className="h-8 flex-1 rounded-md border border-border bg-background px-2 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
        />

        <label className="flex cursor-pointer items-center gap-1.5 text-xs">
          <input
            type="checkbox"
            checked={alert.enabled}
            onChange={(e) => onChange({ enabled: e.target.checked })}
            className="h-3.5 w-3.5 cursor-pointer accent-primary"
          />
          {t("aktiv")}
        </label>

        <button
          type="button"
          onClick={onRemove}
          aria-label={t("Channel entfernen")}
          className="rounded-md p-1.5 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </header>

      {expanded ? (
        <div className="grid grid-cols-1 gap-4 border-t border-border p-4 md:grid-cols-2">
          <div className="flex flex-col gap-1 md:col-span-2">
            <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              {t("Kanal-Typ")}
            </span>
            <div className="flex flex-wrap gap-1">
              {CHANNEL_TYPES.map((ct) => (
                <button
                  key={ct.value}
                  type="button"
                  onClick={() => onChange({ channel_type: ct.value })}
                  className={
                    "rounded-full px-3 py-1 text-[11px] font-medium transition-colors " +
                    (alert.channel_type === ct.value
                      ? "bg-primary text-primary-foreground"
                      : "border border-border bg-card text-muted-foreground hover:bg-muted")
                  }
                >
                  {ct.label}
                </button>
              ))}
            </div>
          </div>

          {alert.channel_type === "email" ? (
            <label className="flex flex-col gap-1 md:col-span-2">
              <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                {t("Empfänger")}
              </span>
              <input
                type="text"
                value={alert.email_to}
                onChange={(e) => onChange({ email_to: e.target.value })}
                placeholder="instandhaltung@werk.example, leitstand@werk.example"
                autoComplete="off"
                className="h-9 rounded-md border border-border bg-background px-3 font-mono text-xs outline-none focus:border-primary focus:ring-1 focus:ring-primary"
              />
              <span className="text-[11px] text-muted-foreground">
                {t("Kommagetrennt. Den SMTP-Server konfigurierst du unter Einstellungen → E-Mail.")}
              </span>
            </label>
          ) : alert.channel_type === "nextcloud_talk" ? (
            <>
              <label className="flex flex-col gap-1 md:col-span-2">
                <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                  {t("Link zur Unterhaltung")}
                </span>
                <input
                  type="text"
                  value={alert.webhook_url}
                  onChange={(e) => onChange({ webhook_url: e.target.value })}
                  placeholder="https://cloud.werk.example/call/abc123xy"
                  autoComplete="off"
                  className="h-9 rounded-md border border-border bg-background px-3 font-mono text-xs outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                />
                <span className="text-[11px] text-muted-foreground">
                  {t("So, wie er in Talk in der Adresszeile steht.")}
                </span>
              </label>
              <label className="flex flex-col gap-1 md:col-span-2">
                <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                  {t("Bot-Geheimnis")}
                </span>
                <input
                  type="password"
                  value={alert.talk_secret ?? ""}
                  onChange={(e) => onChange({ talk_secret: e.target.value })}
                  autoComplete="new-password"
                  className="h-9 rounded-md border border-border bg-background px-3 font-mono text-xs outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                />
                <span className="text-[11px] text-muted-foreground">
                  {t("Leif schreibt als Bot. Der Bot wird einmal auf dem Nextcloud-Server angelegt (occ talk:bot:install --feature response) und in der Unterhaltung eingeschaltet.")}
                </span>
              </label>
            </>
          ) : (
            <label className="flex flex-col gap-1 md:col-span-2">
              <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                {t("Webhook-URL")}
              </span>
              <input
                type="password"
                value={alert.webhook_url}
                onChange={(e) => onChange({ webhook_url: e.target.value })}
                placeholder={
                  alert.channel_type === "webhook"
                    ? "https://n8n.werk.example/webhook/leif"
                    : "https://outlook.office.com/webhook/…"
                }
                autoComplete="off"
                className="h-9 rounded-md border border-border bg-background px-3 font-mono text-xs outline-none focus:border-primary focus:ring-1 focus:ring-primary"
              />
              <span className="text-[11px] text-muted-foreground">
                {alert.channel_type === "webhook"
                  ? t("Leif schickt ein flaches JSON-POST (source, alert, level, client_id, message, …) — passt für n8n, Node-RED, Slack-Bridges oder Andon-Steuerungen.")
                  : t("Aus Teams: Channel → „⋯\" → Connectors → Incoming Webhook.")}
              </span>
            </label>
          )}

          <label className="flex flex-col gap-1">
            <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              {t("Regex-Pattern (eines pro Zeile)")}
            </span>
            <textarea
              value={patternsText}
              onChange={(e) => onChange({ patterns: e.target.value.split("\n") })}
              rows={5}
              placeholder={"connection.*failed\nout of memory"}
              className="resize-y rounded-md border border-border bg-background p-2 font-mono text-xs outline-none focus:border-primary focus:ring-1 focus:ring-primary"
            />
            <span className="text-[11px] text-muted-foreground">
              {t("Min. ein Pattern muss am Log-Message matchen, damit der Alert feuert.")}
            </span>
          </label>

          <div className="flex flex-col gap-1">
            <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              {t("Log-Level")}
            </span>
            <div className="flex flex-wrap gap-1">
              {ALERT_LEVELS.map((level) => {
                const active = alert.levels.includes(level);
                return (
                  <button
                    key={level}
                    type="button"
                    onClick={() => toggleLevel(level)}
                    className={
                      "rounded-full px-2.5 py-0.5 text-[11px] font-medium tabular-nums transition-colors " +
                      (active
                        ? "bg-primary text-primary-foreground"
                        : "border border-border bg-card text-muted-foreground hover:bg-muted")
                    }
                  >
                    {level}
                  </button>
                );
              })}
            </div>
            <span className="text-[11px] text-muted-foreground">
              {t("ANY = alle Level. Ein Klick auf ein einzelnes Level ersetzt ANY.")}
            </span>
          </div>

          <div className="flex flex-col gap-1 md:col-span-2">
            <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              {t("Grace Periods")}
            </span>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <label className="flex flex-col gap-1">
                <span className="text-[11px] text-muted-foreground">
                  {t("Zusammenfassen für (Sek.)")}
                </span>
                <input
                  type="number"
                  min={0}
                  value={effectiveCollapseWindow(alert)}
                  onChange={(e) =>
                    onChange({
                      collapse_window_sec: e.target.value === "" ? null : Math.max(0, parseInt(e.target.value, 10) || 0),
                      dedupe_window_sec: null,
                    })
                  }
                  className="h-9 rounded-md border border-border bg-background px-3 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-[11px] text-muted-foreground">
                  {t("Max. Meldungen pro Minute")}
                </span>
                <input
                  type="number"
                  min={0}
                  value={effectiveMaxPerMinute(alert)}
                  onChange={(e) =>
                    onChange({
                      max_per_minute: e.target.value === "" ? null : Math.max(0, parseInt(e.target.value, 10) || 0),
                      cooldown_sec: null,
                    })
                  }
                  className="h-9 rounded-md border border-border bg-background px-3 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-[11px] text-muted-foreground">
                  {t("Erst senden, wenn Fehler anhält seit (Sek.)")}
                </span>
                <input
                  type="number"
                  min={0}
                  value={alert.min_pending_sec ?? 0}
                  onChange={(e) =>
                    onChange({
                      min_pending_sec: e.target.value === "" ? null : Math.max(0, parseInt(e.target.value, 10) || 0),
                    })
                  }
                  className="h-9 rounded-md border border-border bg-background px-3 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                />
              </label>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={alert.summary_on_storm_end ?? false}
                onChange={(e) => onChange({ summary_on_storm_end: e.target.checked })}
                className="h-4 w-4 rounded border-border accent-primary"
              />
              <span>{t("Abschlussmeldung bei Sturmende")}</span>
              <span className="text-[11px] text-muted-foreground">{t("(„×247 in 4m12s — zuletzt: …“)")}</span>
            </label>
            <span className="text-[11px] text-muted-foreground">
              {t("Gleiche Meldung je Client (Zeitstempel, IDs und Zahlen ab zwei Stellen werden ignoriert): Erstmeldung sofort, weitere Treffer werden bis N Sek. nach dem letzten Auftreten zusammengefasst — maximal 2 Nachrichten pro Sturm. Das Rate-Limit begrenzt die Regel insgesamt; Überzählige erscheinen als „(+N unterdrückt)\". „Anhält seit\": nur senden, wenn der Fehler innerhalb der Wartezeit erneut auftritt — einmalige Ausreißer werden verworfen. 0 = aus.")}
            </span>
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-2 border-t border-border px-4 py-2 text-[11px] text-muted-foreground">
          <span>{CHANNEL_TYPES.find((ct) => ct.value === alert.channel_type)?.label ?? "Microsoft Teams"}</span>
          <span>·</span>
          <span>
            {alert.patterns.filter(Boolean).length === 1
              ? t("1 Pattern")
              : t("{n} Pattern", { n: alert.patterns.filter(Boolean).length })}
          </span>
          <span>·</span>
          <span>{t("Level: {levels}", { levels: alert.levels.join(", ") })}</span>
          <span>·</span>
          {alert.channel_type === "email" ? (
            <span className={alert.email_to ? "" : "text-warning"}>
              {alert.email_to ? t("Empfänger gesetzt") : t("Empfänger fehlen")}
            </span>
          ) : alert.channel_type === "nextcloud_talk" ? (
            <span className={alert.webhook_url && alert.talk_secret ? "" : "text-warning"}>
              {alert.webhook_url && alert.talk_secret
                ? t("Unterhaltung gesetzt")
                : t("Link oder Geheimnis fehlt")}
            </span>
          ) : (
            <span className={alert.webhook_url ? "" : "text-warning"}>
              {alert.webhook_url ? t("Webhook gesetzt") : t("Webhook fehlt")}
            </span>
          )}
        </div>
      )}
    </section>
  );
}
